import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Compass, Users, Eye, ArrowRight, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { catStyle } from "@/lib/categoryColors";

const CATEGORIES = [
    "Internal Audit Roles and Responsibilities",
    "Risk Management Governance",
    "Risk Management Assurance",
    "Mixed",
];

export default function Discover() {
    const { user } = useAuth();
    const [items, setItems] = useState(null);
    const [activeCat, setActiveCat] = useState("__all__");

    const load = () => api.get("/public/sets").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!items) return items;
        if (activeCat === "__all__") return items;
        if (activeCat === "__uncat__") return items.filter((s) => !s.category);
        return items.filter((s) => s.category === activeCat);
    }, [items, activeCat]);

    const toggleFeature = async (s) => {
        try {
            const { data } = await api.post(`/admin/sets/${s.id}/feature`);
            toast.success(data.featured ? "Featured" : "Unfeatured");
            load();
        } catch {
            toast.error("Failed to update");
        }
    };

    const countByCat = useMemo(() => {
        const m = { __all__: items?.length || 0, __uncat__: 0 };
        for (const c of CATEGORIES) m[c] = 0;
        (items || []).forEach((s) => {
            if (!s.category) m.__uncat__ += 1;
            else if (m[s.category] !== undefined) m[s.category] += 1;
        });
        return m;
    }, [items]);

    return (
        <div className="mx-auto max-w-7xl px-6 py-16" data-testid="discover-page">
            <div className="mx-auto max-w-2xl text-center">
                <div className="overline mb-3 text-primary flex items-center justify-center gap-2">
                    <Compass weight="duotone" size={16} /> Community
                </div>
                <h1 className="font-display text-5xl font-semibold tracking-tight">Discover shared study sets.</h1>
                <p className="mt-4 text-muted-foreground">Curated question packs published by CRMA candidates. Preview any set — save it to your own collection with one click.</p>
            </div>

                {items && items.length > 0 && (
                    <div className="mt-12 flex flex-wrap justify-center gap-2" data-testid="discover-filters">
                        <CatChip label={`All (${countByCat.__all__})`} active={activeCat === "__all__"} onClick={() => setActiveCat("__all__")} testid="cat-all" dotClass="bg-primary" />
                        {CATEGORIES.map((c) => (
                            countByCat[c] > 0 && (
                                <CatChip key={c} label={`${catStyle(c).short} (${countByCat[c]})`} active={activeCat === c} onClick={() => setActiveCat(c)} testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`} dotClass={catStyle(c).dot} />
                            )
                        ))}
                        {countByCat.__uncat__ > 0 && (
                            <CatChip label={`Uncategorized (${countByCat.__uncat__})`} active={activeCat === "__uncat__"} onClick={() => setActiveCat("__uncat__")} testid="cat-uncat" dotClass="bg-muted-foreground/50" />
                        )}
                    </div>
                )}

            {items === null && <div className="mt-16 text-center text-muted-foreground">Loading…</div>}
            {items?.length === 0 && (
                <div className="mx-auto mt-16 max-w-lg border border-dashed border-border bg-card p-8 text-center" data-testid="discover-empty">
                    <Users weight="duotone" size={28} className="mx-auto text-primary" />
                    <p className="mt-3 text-muted-foreground">No public sets yet. Be the first — save a session as a set from the results screen and share it.</p>
                </div>
            )}

            {filtered && filtered.length > 0 && (
                <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="discover-grid">
                    {filtered.map((s) => {
                        const cs = catStyle(s.category);
                        return (
                        <div key={s.share_token} className={`group relative flex flex-col justify-between border bg-card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg ${cs.accent} ${s.featured ? "border-primary" : "border-border"}`} data-testid={`discover-${s.share_token}`}>
                            {s.featured && (
                                <div className="absolute -top-2 -right-2 flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground" data-testid={`featured-badge-${s.share_token}`}>
                                    <Star weight="fill" size={12} /> Featured
                                </div>
                            )}
                            <div>
                                <div className="flex items-start gap-3">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-sm border border-border bg-secondary/60 text-2xl" data-testid={`emoji-${s.share_token}`}>
                                        {s.emoji || "📚"}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="overline text-muted-foreground">Study set · {s.count} questions</div>
                                        <h3 className="mt-1 font-display text-xl font-semibold tracking-tight">{s.name}</h3>
                                        {s.category && (
                                            <div className={`mt-2 inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cs.badge}`} data-testid={`cat-badge-${s.share_token}`}>
                                                {s.category}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                                    <span>by {s.author}</span>
                                    <span className="inline-flex items-center gap-1"><Eye size={12} /> {s.view_count || 0}</span>
                                    {s.created_at && <span>· {new Date(s.created_at).toLocaleDateString()}</span>}
                                </div>
                            </div>
                            <div className="mt-6 flex gap-2">
                                <Link to={`/shared/${s.share_token}`} className="flex-1">
                                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground" data-testid={`discover-open-${s.share_token}`}>
                                        Open set <ArrowRight size={14} className="ml-2" />
                                    </Button>
                                </Link>
                                {user?.role === "admin" && (
                                    <Button
                                        variant={s.featured ? "default" : "ghost"}
                                        onClick={() => toggleFeature(s)}
                                        className={s.featured ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}
                                        title={s.featured ? "Unfeature" : "Feature this set"}
                                        data-testid={`feature-toggle-${s.share_token}`}
                                    >
                                        <Star weight={s.featured ? "fill" : "regular"} size={14} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    );})}
                </div>
            )}
            {items && items.length > 0 && filtered.length === 0 && (
                <div className="mt-10 text-center text-muted-foreground" data-testid="filter-empty">No sets in this category yet.</div>
            )}
        </div>
    );
}

function CatChip({ label, active, onClick, testid, dotClass }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}
            data-testid={testid}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${dotClass || "bg-primary"}`} />
            {label}
        </button>
    );
}
